import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { i18n } from '@/i18n';

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/v1`;

let accessToken: string | null = null;
let onUnauthorizedHandler: (() => void) | null = null;

let refreshPromise: Promise<RefreshResult | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function onUnauthorized(handler: () => void) {
  onUnauthorizedHandler = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

export interface RefreshResult {
  accessToken: string;
  user: unknown;
}

async function callRefresh(): Promise<RefreshResult | null> {
  try {
    const res = await axios.post<{ data: RefreshResult }>(
      `${API_BASE}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const next = res.data?.data ?? null;
    if (next?.accessToken) accessToken = next.accessToken;
    return next;
  } catch {
    return null;
  }
}

/**
 * The ONE way to renew the session, shared by the 401 interceptor and by the
 * session bootstrap on page load.
 *
 * The refresh token is single-use and rotates on every call, so two concurrent
 * refreshes mean the second one presents an already-revoked token, receives a
 * 401 and logs a perfectly valid user out — which is exactly what happened when
 * the page bootstrap and a background poller renewed at the same moment.
 * Every caller now waits on the same promise, and it is cleared only once it
 * has settled, never in the window in between.
 */
export function refreshSession(): Promise<RefreshResult | null> {
  refreshPromise ??= callRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Don't try to refresh the refresh / logout / login endpoints themselves.
    const url = (config?.url ?? '') as string;
    const isAuthEndpoint = url.startsWith('/auth/');

    if (status === 401 && !config?._retry && !isAuthEndpoint) {
      config._retry = true;

      const next = await refreshSession();

      if (next?.accessToken) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${next.accessToken}`;
        return api.request(config);
      }

      if (onUnauthorizedHandler) onUnauthorizedHandler();
    }

    return Promise.reject(error);
  },
);

/**
 * Build a user-facing error message from an axios error.
 * Always returns something descriptive — never "Something went wrong".
 */
/**
 * The message the user reads, in the language they chose. The server answers in
 * English, so a code we know how to phrase wins over the server's own wording;
 * for any other code the server's message is still shown, because a vague
 * translated sentence would say less than an exact untranslated one.
 */
function messageForCode(code: string | undefined, fallback: string): string {
  if (!code) return fallback;
  const key = `errors.byCode.${code}`;
  const translated = i18n.global.t(key);
  return translated === key ? fallback : translated;
}

export function describeError(error: unknown): { message: string; code?: string; field?: string } {
  const t = i18n.global.t;

  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as
      | { error?: { code?: string; message?: string; field?: string } }
      | undefined;
    if (payload?.error?.message) {
      return {
        message: messageForCode(payload.error.code, payload.error.message),
        code: payload.error.code,
        field: payload.error.field,
      };
    }
    if (error.code === 'ERR_NETWORK') {
      return { message: t('errors.network'), code: 'NETWORK_ERROR' };
    }
    if (error.code === 'ECONNABORTED') {
      return { message: t('errors.timeout'), code: 'TIMEOUT' };
    }
    if (error.response?.status) {
      return {
        message: t('errors.http', { status: error.response.status }),
        code: 'HTTP_ERROR',
      };
    }
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: t('errors.generic') };
}
